/*
 *   Copyright (c) 2025 2023 Celadon Development LLC
 *   All rights reserved.
 *   Author Aliaksandr Ziabko <aliaksandr.ziabko@celadonsoft.com>
 */
import api from "./api";

export interface HtpVideo {
  url: string;
  thumbnail?: string;
}

export interface HtpCategory {
  id: number;
  name: string;
  slug: string;
}

export interface HowToPerform {
  id: number;
  name: string;
  description?: string;
  imageUrls?: string[];
  videoUrls?: HtpVideo[];
  categories: HtpCategory[];
  createdAt: string;
  updatedAt: string;
}

export interface HowToPerformResponse {
  data: HowToPerform[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface GetHowToPerformParams {
  page?: number;
  limit?: number;
  name?: string[];
  categoriesIds?: string[];
}

/* =========================
   GET LIST
========================= */
export const getHowToPerforms = async (params?: GetHowToPerformParams) => {
  try {
    const {
      page = 1,
      limit = 10,
      name = [],
      categoriesIds = [],
    } = params || {};

    const response = await api.get<HowToPerformResponse>("/how-to-perform", {
      params: {
        page,
        limit,
        ...(name.length > 0 && { "name[]": name }),
        ...(categoriesIds.length > 0 && { "categoriesIds[]": categoriesIds }),
      },
      timeout: 0,
      paramsSerializer: (params) => {
        const searchParams = new URLSearchParams();
        Object.keys(params).forEach((key) => {
          const value = params[key];
          if (Array.isArray(value)) {
            value.forEach((v) => searchParams.append(`${key}[]`, v));
          } else {
            searchParams.append(key, value);
          }
        });
        return searchParams.toString();
      },
    });

    return response.data;
  } catch (error) {
    console.error("Error fetching HTP:", error);
    throw error;
  }
};

export const searchHTP = async (
  searchType: "name" | "categoryName",
  query: string,
): Promise<{ id: number; name: string }[]> => {
  if (!query.trim()) return [];

  try {
    const params =
      searchType === "name" ? { "name[]": query } : { "categoryName[]": query };

    const response = await api.get<HowToPerformResponse>("/how-to-perform", {
      params,
      paramsSerializer: (params) => {
        const searchParams = new URLSearchParams();
        Object.keys(params).forEach((key) => {
          const value = params[key];
          if (Array.isArray(value)) {
            value.forEach((v) => searchParams.append(`${key}[]`, v));
          } else {
            searchParams.append(key, value);
          }
        });
        return searchParams.toString();
      },
    });

    switch (searchType) {
      case "name":
        return response.data.data.map((htp) => ({
          id: htp.id,
          name: htp.name,
        }));
      case "categoryName":
        // получаем уникальные категории
        const categoriesMap: Record<number, string> = {};
        response.data.data.forEach((htp) =>
          htp.categories.forEach((c) => (categoriesMap[c.id] = c.name)),
        );
        return Object.entries(categoriesMap).map(([id, name]) => ({
          id: Number(id),
          name,
        }));
      default:
        return [];
    }
  } catch (error) {
    console.error(`Error searching HTP by ${searchType}:`, error);
    return [];
  }
};

/* =========================
   GET ONE
========================= */
export const getHtpById = async (id: number): Promise<HowToPerform> => {
  const { data } = await api.get<HowToPerform>(`/how-to-perform/${id}`);
  return data;
};

/* =========================
   GET CATEGORIES
========================= */
export const getHtpCategories = async (): Promise<HtpCategory> => {
  const { data } = await api.get<HtpCategory>(`/htp-category`);
  return data;
};

/* =========================
   CREATE 
========================= */
export const createHowToPerform = async (payload: {
  name: string;
  description?: string;
  categoriesIds: number[];
  imageUrls?: string[];
  videoUrls?: {
    url: string;
    thumbnail?: string;
  }[];
}): Promise<HowToPerform> => {
  const { data } = await api.post<HowToPerform>("/how-to-perform", payload);
  return data;
};

/* =========================
   UPDATE 
========================= */
export const updateHtp = async (
  id: number,
  payload: {
    name?: string;
    description?: string;
    categoriesIds?: number[];
    imageUrls?: string[];
    videoUrls?: {
      url: string;
      thumbnail?: string;
    }[];
  },
): Promise<HowToPerform> => {
  const { data } = await api.patch<HowToPerform>(
    `/how-to-perform/${id}`,
    payload,
  );
  return data;
};

/* =========================
   DELETE
========================= */
export const deleteHowToPerform = async (id: number): Promise<void> => {
  await api.delete(`/how-to-perform/${id}`);
};

/* =========================
   UPLOAD
========================= */
export const uploadHtpMedia = async (
  htpId: number,
  file: File,
  onProgress?: (progress: number) => void,
): Promise<{ url: string; thumbnail?: string }> => {
  const formData = new FormData();
  formData.append("file", file);

  const { data } = await api.post(`/how-to-perform/${htpId}/upload`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 60000011111,
    onUploadProgress: (progressEvent) => {
      if (onProgress) {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / (progressEvent.total || 1),
        );
        onProgress(percentCompleted);
      }
    },
  });

  return data;
};
